import { create, sv, VNode, el } from "@dhx/ts-common/dom";
import { Chart } from "./Chart";
import { calcPointRef, getFontStyle, getTextWidth, verticalCenteredText } from "./helpers/common";
import { ChartEvents, ChartType, TooltipType } from "./types";
import { View } from "@dhx/ts-common/view";

const getAttrs = function(textWidth: number, x: number, y: number, type: TooltipType, chartType: ChartType) {
	const dy = chartType === ChartType.bar ? 5 : 0;
	switch(type) {
		case TooltipType.top: {
			const h1 = (textWidth + 20 - 8 - 4) / 2;
			const h2 = textWidth + 20 - 4;
			const d = `M0 0 l4 -4 h${h1} a2 2 0 0 0 2 -2 v-18 a2 2 0 0 0 -2 -2 h${-h2} a2 2 0 0 0 -2 2 v18 a2 2 0 0 0 2 2 h${h1} Z`;
			const textX = 0;
			const textY = -15;
			return {
				d,
				left: x,
				top: y - 6 + dy,
				textX,
				textY
			};
		}
		case TooltipType.bot: {
			const h1 = (textWidth + 20 - 8 - 4) / 2;
			const h2 = textWidth + 20 - 4;
			const d = `M0 0 l4 4 h${h1} a2 2 0 0 1 2 2 v18 a2 2 0 0 1 -2 2 h${-h2} a2 2 0 0 1 -2 -2 v-18 a2 2 0 0 1 2 -2 h${h1} Z`;
			const textX = 0;
			const textY = 15;
			return {
				d,
				left: x,
				top: y + 6 - dy,
				textX,
				textY
			};
		}
		case TooltipType.right: {
			const h = textWidth + 20 - 4;
			const d = `M0 0 l4 -4 v-5 a2 2 0 0 1 2 -2 h${h} a2 2 0 0 1 2 2 v18 a2 2 0 0 1 -2 2 h${-h} a2 2 0 0 1 -2 -2 v-5 Z`;
			const textX = h / 2 + 6;
			const textY = 0;
			return {
				d,
				left: x + 1,
				top: y,
				textX,
				textY
			};
		}
		case TooltipType.left: {
			const h = textWidth + 20 - 4;
			const d = `M0 0 l-4 -4 v-5 a2 2 0 0 0 -2 -2 h${-h} a2 2 0 0 0 -2 2 v18 a2 2 0 0 0 2 2 h${h} a2 2 0 0 0 2 -2 v-5 Z`;
			const textX = -h / 2 - 6;
			const textY = 0;
			return {
				d,
				left: x - 1,
				top: y,
				textX,
				textY
			};
		}
		case TooltipType.simple: {
			const h = textWidth + 20 - 4;
			const d = `M0 0 v-4 a2 2 0 0 1 2 -2 h${h} a2 2 0 0 1 2 2 v18 a2 2 0 0 1 -2 2 h${-h} a2 2 0 0 1 -2 -2 v-6 Z`;
			const textX = h / 2 + 2;
			const textY = 6;
			return {
				d,
				left: x - h / 2 - 2,
				top: y - 5,
				textX,
				textY
			};
		}
	}
};

interface ITooltipState {
	leftOffset: number;
	topOffset: number;
	value: string;
	x: number;
	y: number;
	type: TooltipType;
	chartType: ChartType;
	isVisible: boolean;
}

export class Tooltip extends View {
	private _lastPointRef: string;
	private _chart: Chart;
	private _state: ITooltipState;

	constructor(container, config) {
		super(container, config);
		this._chart = config.chart;

		this._state = {leftOffset: 0, topOffset: 0, value: "", x: 0, y: 0, type: null, chartType: null, isVisible: false};
		this.mount(container, create({render: () => this._draw()}));

		this._chart.events.on(ChartEvents.chartMouseMove, (x, y, left, top) => {
			const closest: [number, number, number, string, string] = [Infinity, null, null, null, null]; // (dist, x, y, id, serieid)
			this._chart.eachSeries(serie => {
				const serieClosest = serie.getClosest(x, y);
				if (closest[0] > serieClosest[0]) {
					closest[0] = serieClosest[0];
					closest[1] = serieClosest[1];
					closest[2] = serieClosest[2];
					closest[3] = serieClosest[3];
					closest[4] = serie.id;
				}
			});
			const tooltipSeries = this._chart.getSeries(closest[4]);
			if (tooltipSeries) {
				const ref = calcPointRef(closest[3], closest[4]);
				if (ref === this._lastPointRef) {
					return;
				}

				const text = tooltipSeries.getTooltipText(closest[3]);
				if (text) {
					const tooltipType = tooltipSeries.getTooltipType(closest[3], closest[1], closest[2]);
					this._enableActivePoint(ref);
					this._state.leftOffset = left;
					this._state.topOffset = top;
					this._state.value = text;
					this._state.x = closest[1];
					this._state.y = closest[2];
					this._state.type = tooltipType;
					this._state.chartType = tooltipSeries.config.type;
					this._state.isVisible = true;
					this.paint();
				}
			}
		}, this);

		this._chart.events.on(ChartEvents.chartMouseLeave, () => this._hide(), this);
	}
	destructor() {
		this._chart.events.detach(ChartEvents.chartMouseLeave, this);
		this._chart.events.detach(ChartEvents.chartMouseMove, this);
		this.unmount();
	}

	private _hide() {
		this._disableLastActivePoint();
		this._state.isVisible = false;
		this.paint();
	}
	private _enableActivePoint(ref: string) {
		const rootView = this._chart.getRootView();

		const point = rootView && rootView.refs && rootView.refs[ref];
		if (point) {
			this._disableLastActivePoint();
			this._lastPointRef = ref;
			point.patch({class: point.attrs.class + " active-figure"});
		}
	}
	private _disableLastActivePoint() {
		if (this._lastPointRef) {
			const rootView = this._chart.getRootView();
			const point = rootView && rootView.refs && rootView.refs[this._lastPointRef];
			if (point) {
				point.patch({class: point.attrs.class.replace(" active-figure", "")});
			}
			this._lastPointRef = null;
		}
	}
	private _draw(): VNode {
		const {value, x, y, type, chartType, isVisible} = this._state;
		let attrs: any;

		if (isVisible && value !== ""){
			const style = getFontStyle("tooltip-text");
			const textWidth = getTextWidth(value, style);
			attrs = getAttrs(
				textWidth,
				x,
				y,
				type || TooltipType.top,
				chartType
			);
		} else {
			attrs = { textX:0, textY:0, d:null, left: 0, top: 0 };
		}
		return el(".dhx_chart.tooltip-container", {
			style: {
				pointerEvents: "none",
				width: 0,
				height: 0,
				visibility: isVisible ? "visible" : "hidden",
				position: "absolute",
				left: attrs.left + this._state.leftOffset + "px",
				top: attrs.top + this._state.topOffset + "px"
			}
		}, isVisible ? [
			sv("svg", [
				sv("path", { d:attrs.d, class: "tooltip-form" }),
				sv("text", { x:attrs.textX, y:attrs.textY, class: "tooltip-text" }, [
					verticalCenteredText(value)
				])
			])
		] : null);
	}
}